import React, { useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/browser';
import { useUserContext } from '../../contexts/UserContext';
import {
    listAllFormTemplateGroups,
    listFormTemplatesByGroup,
    updateFormTemplateGroup,
    deleteFormTemplateGroup as deleteFormTemplateGroupMutation,
    deleteFormTemplate as deleteFormTemplateMutation,
} from '../../graphql/resourceLibraryQueries';
import Add from '@mui/icons-material/Add';
import { AssignmentModal } from './AssignmentModal';
import { CreateEditGroupModal } from './CreateEditGroupModal';
import { TemplatesListModal } from './TemplatesListModal';
import { DeleteGroupModal } from './DeleteGroupModal';
import { Btn, Card, IconShare, TEIQspinner } from '../ui';
import { IconEdit, IconTrash } from '@components/ui';
import '../../css/ResourceLibraryContent.css';

interface FormTemplateGroupItem {
    id: string;
    name: string;
    description?: string | null;
    sharedWithCompanyIds?: (string | null)[] | null;
    resourceLibraryID?: string | null;
    owner?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

interface FormTemplateItem {
    id: string;
    templateName: string;
    inspectionForm?: boolean | null;
    dailyServiceLogForm?: boolean | null;
    pmServiceScheduleForm?: boolean | null;
    createdByCompanyOwnerId?: string | null;
    originalCompanyId?: string | null;
    formTemplateGroupID: string;
    createdAt?: string;
    updatedAt?: string;
}

interface GroupWithCount extends FormTemplateGroupItem {
    templateCount: number;
    templates: FormTemplateItem[];
}

export const FormTemplatesTab: React.FC = () => {
    const { companyOwner } = useUserContext();

    const [groups, setGroups] = useState<GroupWithCount[]>([]);
    const [loading, setLoading] = useState(true);

    // Modal state
    const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<FormTemplateGroupItem | null>(null);
    const [templatesModalGroup, setTemplatesModalGroup] = useState<GroupWithCount | null>(null);
    const [deleteModalGroup, setDeleteModalGroup] = useState<GroupWithCount | null>(null);
    const [assignModalGroup, setAssignModalGroup] = useState<GroupWithCount | null>(null);

    const fetchGroups = useCallback(async () => {
        setLoading(true);
        try {
            const allGroups = (await listAllFormTemplateGroups()) as FormTemplateGroupItem[];
            // Fetch template counts for each group
            const groupsWithCounts: GroupWithCount[] = await Promise.all(
                allGroups.map(async (group) => {
                    try {
                        const templates = (await listFormTemplatesByGroup(group.id)) as FormTemplateItem[];
                        return { ...group, templateCount: templates.length, templates };
                    } catch {
                        return { ...group, templateCount: 0, templates: [] };
                    }
                }),
            );
            setGroups(groupsWithCounts);
        } catch (err) {
            console.error('Error fetching form template groups:', err);
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    const handleCreateClick = () => {
        setEditingGroup(null);
        setCreateEditModalOpen(true);
    };

    const handleEditClick = (group: FormTemplateGroupItem) => {
        setEditingGroup(group);
        setCreateEditModalOpen(true);
    };

    const handleCreateEditClose = () => {
        setCreateEditModalOpen(false);
        setEditingGroup(null);
    };

    const handleCreateEditSave = () => {
        setCreateEditModalOpen(false);
        setEditingGroup(null);
        fetchGroups();
    };

    const handleTemplatesClick = (group: GroupWithCount) => {
        setTemplatesModalGroup(group);
    };

    const handleTemplatesClose = () => {
        setTemplatesModalGroup(null);
        fetchGroups();
    };

    const handleDeleteClick = (group: GroupWithCount) => {
        setDeleteModalGroup(group);
    };

    const handleDeleteClose = () => {
        setDeleteModalGroup(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModalGroup) return;
        try {
            // Cascade delete all templates in the group
            for (const template of deleteModalGroup.templates) {
                await deleteFormTemplateMutation(template.id);
            }
            // Delete the group itself
            await deleteFormTemplateGroupMutation(deleteModalGroup.id);
            setDeleteModalGroup(null);
            fetchGroups();
        } catch (err) {
            console.error('Error deleting group:', err);
            Sentry.captureException(err);
            throw err;
        }
    };

    const handleAssignClick = (group: GroupWithCount) => {
        setAssignModalGroup(group);
    };

    const handleAssignSave = async (companyIds: string[]) => {
        if (!assignModalGroup) return;
        await updateFormTemplateGroup({
            id: assignModalGroup.id,
            sharedWithCompanyIds: companyIds,
        });
        fetchGroups();
    };

    const isCreatedByCurrentOwner = (group: FormTemplateGroupItem) => {
        return group.owner === companyOwner?.id;
    };

    if (loading) {
        return (
            <div className="zrl-loading">
                <TEIQspinner label="Loading form template groups" />
            </div>
        );
    }

    return (
        <>
            {/* Section Header */}
            <div className="zrl-section-header">
                <h2 className="zrl-section-title">
                    Form Template Groups
                    <span className="zrl-count-badge">{groups.length}</span>
                </h2>
                <button className="zrl-btn-add" onClick={handleCreateClick}>
                    <Add fontSize="inherit" />
                    Create Template Group
                </button>
            </div>

            {/* Collapsible Template Lists */}
            {groups.length === 0 ? (
                <div className="zrl-empty">No template groups yet. Click "+ Create Template Group" to get started.</div>
            ) : (
                <Card variant="card-lg" cardClasses="card-pad-0">
                    <div className="zrl-table-wrapper">
                        <table className="zrl-table zrl-table-forms">
                            <thead>
                                <tr>
                                    <th>Group Name</th>
                                    <th>Templates</th>
                                    <th>Created By</th>
                                    <th>Assign to Companies</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map((group) => (
                                    <tr key={group.id}>
                                        <td>
                                            <div className="zrl-table-name">{group.name}</div>
                                            {group.description && (
                                                <div className="zrl-table-desc">{group.description}</div>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                className="zrl-table-link"
                                                aria-label={`View templates in ${group.name}`}
                                                onClick={() => handleTemplatesClick(group)}
                                            >
                                                <span className="zrl-count-badge zrl-badge-blue">
                                                    {group.templateCount} templates
                                                </span>
                                            </button>
                                        </td>
                                        <td>{isCreatedByCurrentOwner(group) ? 'You' : 'Admin'}</td>
                                        <td>
                                            <Btn
                                                variant="outlined-faint"
                                                buttonSize="sm"
                                                buttonIcon={<IconShare />}
                                                buttonText="Manage Assignment"
                                                buttonOnClick={() => handleAssignClick(group)}
                                            />
                                        </td>
                                        <td>
                                            <div className="zrl-actions">
                                                <button
                                                    className="zrl-action-btn"
                                                    title="Edit group"
                                                    aria-label={`Edit ${group.name}`}
                                                    onClick={() => handleEditClick(group)}
                                                >
                                                    <IconEdit />
                                                </button>
                                                <button
                                                    className="zrl-action-btn danger"
                                                    title="Delete group"
                                                    aria-label={`Delete ${group.name}`}
                                                    onClick={() => handleDeleteClick(group)}
                                                >
                                                    <IconTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Create/Edit Group Modal */}
            <CreateEditGroupModal
                isOpen={createEditModalOpen}
                onClose={handleCreateEditClose}
                onSave={handleCreateEditSave}
                editingGroup={editingGroup}
            />

            {/* Templates List Modal */}
            {templatesModalGroup && (
                <TemplatesListModal
                    isOpen={!!templatesModalGroup}
                    onClose={handleTemplatesClose}
                    groupName={templatesModalGroup.name}
                    groupId={templatesModalGroup.id}
                    templates={templatesModalGroup.templates}
                />
            )}

            {/* Delete Group Modal */}
            {deleteModalGroup && (
                <DeleteGroupModal
                    isOpen={!!deleteModalGroup}
                    onClose={handleDeleteClose}
                    onDelete={handleDeleteConfirm}
                    groupName={deleteModalGroup.name}
                    templateCount={deleteModalGroup.templateCount}
                    assignmentCount={
                        deleteModalGroup.sharedWithCompanyIds
                            ? deleteModalGroup.sharedWithCompanyIds.filter(Boolean).length
                            : 0
                    }
                />
            )}

            {/* Assignment Modal */}
            {assignModalGroup && (
                <AssignmentModal
                    isOpen={!!assignModalGroup}
                    onClose={() => {
                        setAssignModalGroup(null);
                    }}
                    resourceName={assignModalGroup.name}
                    resourceType="group"
                    currentCompanyIds={(assignModalGroup.sharedWithCompanyIds?.filter(Boolean) as string[]) || []}
                    onSave={handleAssignSave}
                />
            )}
        </>
    );
};
